import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import { createLogger } from '../../utils/debugUtils';
import { authenticatedFetch } from '../../utils/apiUtils';

// Initialize logger for debugging
const logger = createLogger('ResearchResults');

// Example of logger usage for future reference
// logger.debug('Results component mounted');
// logger.info('Exporting research results to CSV');
// logger.error('Error rendering results', error);

/**
 * Research Results Component
 * Displays the results of a completed research job
 */
function ResearchResults({ jobId, results }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [summary, setSummary] = useState({
    totalWebsites: 0,
    kept: 0,
    excluded: 0
  });
  
  // Debug log when component renders
  console.log('ResearchResults component rendering with:', { jobId, results });
  console.log('Results is null?', results === null);
  console.log('Results is undefined?', results === undefined);
  console.log('Results type:', results ? typeof results : 'N/A');
  console.log('Results keys:', results ? Object.keys(results) : 'N/A');
  console.log('Has websites array?', results?.websites ? true : false);
  console.log('Websites array length:', results?.websites?.length || 0);

  // Calculate summary statistics when results change
  useEffect(() => {
    if (!results || !results.websites) return;

    const totalWebsites = results.websites.length;
    const kept = results.websites.filter(site => site.decision === 'KEEP').length;
    const excluded = results.websites.filter(site => site.decision === 'EXCLUDE').length;

    setSummary({
      totalWebsites,
      kept,
      excluded
    });
  }, [results]);

  // Handle table pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Export results to CSV using backend endpoint
  const handleExportResults = () => {
    if (!jobId) {
      logger.error('Cannot export without a valid job ID');
      return;
    }

    logger.info(`Exporting results for job ${jobId}`);
    
    // Create export URL that refers to our new backend endpoint
    const exportUrl = `/api/research/jobs/${jobId}/export`;
    
    // Create and trigger a download via fetch request using authenticatedFetch
    authenticatedFetch(exportUrl)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Export failed with status: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `research-results-${jobId}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        logger.info('CSV export completed successfully');
      })
      .catch(error => {
        logger.error('Error exporting CSV:', error);
        alert(`Failed to export results: ${error.message}`);
      });
  };

  // Render decision chip with appropriate color
  const renderDecisionChip = (decision) => {
    if (decision === 'KEEP') {
      return <Chip 
        icon={<ThumbUpIcon />} 
        label="KEEP" 
        color="primary" 
        size="small" 
        sx={{ fontWeight: 'bold' }}
      />;
    } else {
      return <Chip 
        icon={<ThumbDownIcon />} 
        label="EXCLUDE" 
        color="secondary" 
        size="small" 
        sx={{ fontWeight: 'bold' }}
      />;
    }
  };

  // Format URL for display
  const formatDisplayUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url;
    }
  };

  return (
    <Box>
      {results && results.websites ? (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Total Websites
                  </Typography>
                  <Typography variant="h3">{summary.totalWebsites}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'rgba(0, 149, 255, 0.08)' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary" gutterBottom>
                    Kept Websites
                  </Typography>
                  <Typography variant="h3" color="primary">{summary.kept}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ bgcolor: 'rgba(232, 62, 140, 0.08)' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="secondary" gutterBottom>
                    Excluded Websites
                  </Typography>
                  <Typography variant="h3" color="secondary">{summary.excluded}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Research Results</Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportResults}
            >
              Export to CSV
            </Button>
          </Box>

          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader aria-label="research results table">
                <TableHead>
                  <TableRow>
                    <TableCell>Website</TableCell>
                    <TableCell>Decision</TableCell>
                    <TableCell>Relevance</TableCell>
                    <TableCell>Reasoning</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.websites
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((website, index) => (
                      <TableRow hover key={index}>
                        <TableCell component="th" scope="row">
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {formatDisplayUrl(website.url)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {renderDecisionChip(website.decision)}
                        </TableCell>
                        <TableCell>
                          {website.relevance_score ? (
                            <Chip
                              label={website.relevance_score + "/10"}
                              color={website.relevance_score >= 7 ? "success" : website.relevance_score >= 4 ? "warning" : "error"}
                              size="small"
                            />
                          ) : "N/A"}
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              maxWidth: 300,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {website.reasoning}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Open website">
                            <IconButton 
                              size="small" 
                              component="a" 
                              href={website.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={results.websites.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>

          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              Job ID: {jobId} • Completed at: {new Date(results.completedAt).toLocaleString()}
            </Typography>
          </Box>
        </>
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Results Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Results will appear here once the research job is completed.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default ResearchResults;
